import { DataAccess } from "../../dataaccess/data.access";
import { ResponseMessage } from "../../dataaccess/data.access";
import { CopySeriesDatabaseProcParams } from "./copy.series.database.service";

export interface SeDemoDataBootstrap {
    allowed?: boolean;
    sessionInstanceId?: string;
    sources?: CopySeriesDatabaseProcParams[];
}

export interface SeDemoDataResetSummaryRow {
    actionKind?: string;
    rowCount?: number;
}

export interface SeDemoDataResetDetailRow {
    actionKind?: string;
    objectType?: string;
    objectId?: string;
    detail?: string;
}

export interface SeDemoDataResetReport {
    message?: string;
    messageCode?: string;
    apply?: boolean;
    summary?: SeDemoDataResetSummaryRow[];
    details?: SeDemoDataResetDetailRow[];
}

const BASE = "/service-portal/se-demo-data";

export class SeDemoDataService {
    public static getBootstrap(): Promise<SeDemoDataBootstrap> {
        return DataAccess.get<ResponseMessage<SeDemoDataBootstrap>>(
            DataAccess.injectCaller(`${BASE}.json`, "SeDemoDataService.getBootstrap"),
        ).then((response) => response.upsertData as SeDemoDataBootstrap);
    }

    public static async clone(sourceInstanceId: string): Promise<string> {
        const response = await DataAccess.post<ResponseMessage>(
            DataAccess.injectCaller(`${BASE}/clone.json`, "SeDemoDataService.clone"),
            { sourceInstanceId },
        );
        return response.message || "Connect25 demo clone completed.";
    }

    public static async resetDemo(startDt: string, endDt: string, apply: boolean): Promise<SeDemoDataResetReport> {
        const response = await DataAccess.post<ResponseMessage<SeDemoDataResetReport>>(
            DataAccess.injectCaller(`${BASE}/reset.json`, "SeDemoDataService.resetDemo"),
            { startDt, endDt, apply },
        );
        return response.upsertData as SeDemoDataResetReport;
    }

    public static formatError(error: { status?: number; data?: ResponseMessage }): string {
        if (error?.status === 401) {
            return "Unauthorized.";
        }
        if (error?.status === 403) {
            return error.data?.message || "This instance is not allowed.";
        }
        if (error?.status === 409) {
            return error.data?.message || "A Connect25 demo clone or reset is already in progress.";
        }
        if (error?.status === 400) {
            return error.data?.message || "Invalid request.";
        }
        return error.data?.message || "Unable to run Connect25 demo data action.";
    }

    public static defaultResetWindow(now = new Date()): { startDt: string; endDt: string } {
        const end = new Date(now);
        end.setSeconds(0, 0);
        end.setMinutes(end.getMinutes() + 1);
        const start = new Date(end);
        start.setMinutes(0, 0, 0);
        start.setHours(start.getHours() - 12);
        return {
            startDt: toDatetimeLocalMinute(start),
            endDt: toDatetimeLocalMinute(end),
        };
    }

    public static datediffHours(startDt: string, endDt: string): number | null {
        if (!startDt || !endDt) {
            return null;
        }
        const start = new Date(startDt);
        const end = new Date(endDt);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end.getTime() <= start.getTime()) {
            return null;
        }
        const startHour = new Date(start);
        startHour.setMinutes(0, 0, 0);
        const endHour = new Date(end);
        endHour.setMinutes(0, 0, 0);
        return (endHour.getTime() - startHour.getTime()) / 3_600_000;
    }
}

function toDatetimeLocalMinute(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hour = String(date.getHours()).padStart(2, "0");
    const minute = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hour}:${minute}`;
}
